import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import type { FeatureGroup } from "./types";
import { ProductFeatureIllustration } from "./ProductFeatureIllustration";

interface ProductFeatureGroupsProps {
  productKey: string;
  featureGroups: FeatureGroup[];
  accentClass?: string;
}

export function ProductFeatureGroups({ productKey, featureGroups, accentClass = "bg-petrol-light" }: ProductFeatureGroupsProps) {
  const { t } = useTranslation();
  const p = `productPages.${productKey}`;

  return (
    <section className="py-12 lg:py-28 bg-secondary/30">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-10 lg:mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
            {t(`${p}.features.title`)}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-body">
            {t(`${p}.features.subtitle`)}
          </p>
        </motion.div>

        <div className="space-y-12 lg:space-y-28">
          {featureGroups.map((group, groupIndex) => (
            <motion.div
              key={group.titleKey}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className={`flex flex-col ${groupIndex % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"} gap-6 lg:gap-16 items-center`}
            >
              {/* Feature list */}
              <div className="flex-1 w-full">
                <span className="inline-block text-xs font-bold font-subheading uppercase tracking-widest text-coral mb-2">
                  {String(groupIndex + 1).padStart(2, "0")}
                </span>
                <h3 className="text-2xl md:text-3xl font-medium font-heading text-primary mb-8">
                  {t(group.titleKey)}
                </h3>
                <div className="space-y-6">
                  {group.features.map((feature, featureIndex) => (
                    <motion.div
                      key={feature.titleKey}
                      initial={{ opacity: 0, x: groupIndex % 2 === 1 ? 20 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: featureIndex * 0.1 }}
                      className="flex gap-4"
                    >
                      <div className="w-10 h-10 rounded-lg bg-petrol-light flex items-center justify-center flex-shrink-0 mt-0.5">
                        <feature.icon className="h-5 w-5 text-primary dark:text-white" />
                      </div>
                      <div>
                        <h4 className="text-base font-bold font-subheading text-foreground mb-1">
                          {t(feature.titleKey)}
                        </h4>
                        <p className="text-sm text-muted-foreground font-body leading-relaxed">
                          {t(feature.descriptionKey)}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Illustration */}
              <div className="flex-1 w-full flex items-center justify-center">
                {group.illustrationKey ? (
                  <ProductFeatureIllustration illustrationKey={group.illustrationKey} />
                ) : (
                  <div className={`relative w-full max-w-sm aspect-square rounded-3xl ${accentClass} p-8 flex items-center justify-center overflow-hidden`}>
                    <div className="absolute top-6 right-6 w-20 h-20 rounded-full bg-primary/5" />
                    <div className="absolute bottom-8 left-8 w-14 h-14 rounded-full bg-coral/10" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full border-2 border-primary/10" />
                    <div className="relative grid grid-cols-2 gap-4">
                      {group.features.slice(0, 4).map((feature) => (
                        <div
                          key={feature.titleKey}
                          className="w-14 h-14 rounded-xl bg-background shadow-sm flex items-center justify-center"
                        >
                          <feature.icon className="h-6 w-6 text-primary dark:text-white" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
